import { Button, Portal } from '@nazka/nazka.mapframe.components'
import React, { useState } from 'react'
import { setUploadModalStatus, setCurrentFlood, setCurrentBuilding } from '@App/components/LayerSelector/layer-selector.redux.action';
import { faXmark } from '@fortawesome/pro-solid-svg-icons'
import { useDispatch } from 'react-redux'

import './upload-modal.styl'
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import queryClient from '@utils/queries/QueryClient';

function UploadModal({ uploadType }) {
  const dispatch = useDispatch()
  const [selectedState, setSelectedState] = useState('desktop')
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  const { mutate, isPending } = useMutation({
    mutationFn: async () => { 
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('name', fileName);
      return axios.post(
        `/api/upload/${uploadType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }, 
        },
      )
    },
    onSuccess: () => {
      toast.success(`${uploadType} has been uploaded`);
      dispatch(setUploadModalStatus(false))
      queryClient.invalidateQueries({ queryKey: [`${uploadType}-records`] })
    },
    onSettled: (data) => {
      if (data && uploadType === 'flood') {
        dispatch(setCurrentFlood(data.data.id))
      } else {
        dispatch(setCurrentBuilding(data.data.id))
      }
    },
    onError: (error) => {
      setErrorMessage(error || 'Something went wrong uploading, please try again')
    },
  })

  const isValidWGS84Coordinate = ([lon, lat]) => lon >= -180 && lon <= 180 && lat >= -90 && lat <= 90;
  
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    const text = await file.text();
    const geojson = JSON.parse(text);

    setErrorMessage(null);
    setSelectedFile(null);
    setFileName('');

    if (!file) return;
    if (file.size / (1024 * 1024) > 5) {
      setErrorMessage('File is too large. Maximum allowed size is 5MB.')
      return
    }
    if (file.length > 1) {
      setErrorMessage('You can only upload 1 file at a time.')
      return
    }
    
    for (let i = 0; i < geojson.features.length; i += 1) {
      const feature = geojson.features[i];
      const { type, coordinates } = feature.geometry;
  
      if (type !== 'Polygon' && type !== 'MultiPolygon') {
        setErrorMessage('All your features must be polygons or multipolygons.');
        return;
      }
  
      const rings = type === 'Polygon' ? coordinates : coordinates.flat();
      for (let j = 0; j < rings.length; j += 1) {
        const ring = rings[j];
        for (let k = 0; k < ring.length; k += 1) {
          const coord = ring[k];
          if (!isValidWGS84Coordinate(coord)) {
            setErrorMessage(
              'You can only upload polygons which use the WGS 84 (EPSG 4326) coordinate system.',
            );
            return;
          }
        }
      }
    }

    setFileName(file.name);
    setSelectedFile(file);
    setErrorMessage(null)
  };

  return (
    <Portal container={document.getElementById('js-mapframe-portal')}>
      <div className="upload-modal__container">
        <div className="upload-modal__wrapper">
          <div className="upload-modal__header">
            <div className="pload-modal__header__tabs">
              <Button
                color={selectedState === 'desktop' ? '#0367A5' : '#ddd'}
                onClick={() => setSelectedState('desktop')}
                css={{
                  borderRadius: '4px 0  0 4px ',
                }}
              >
                Desktop
              </Button>
              <Button
                color={selectedState === 'coreplatform' ? '#0367A5' : '#ddd'}
                onClick={() => setSelectedState('coreplatform')}
                css={{
                  borderRadius: '0 4px 4px 0 ',
                }}
                disabled
              >
                Core platform
              </Button>
            </div>
            <Button
              type="button"
              icon={faXmark}
              color="white"
              onClick={() => dispatch(setUploadModalStatus(false))}
            />
          </div>
          <div className="upload-modal__file-upload__container">
            <h3>{ uploadType === 'building' ? 'Uploading building file' : 'Uploading flood extent'}</h3> 
            <div className="upload-modal__file-upload__wrapper">
              <div className="upload-modal__file-upload__input-title">
                <span className="upload-modal__file-input_placeholder">
                  {(fileName.length > 30 ? `${fileName.substring(0, 30)}...` : fileName) || 'Upload your geojson'}
                </span>
                {errorMessage && <span className="upload-modal__error-message">{errorMessage}</span>}
              </div>
              <input
                type="file"
                accept=".geojson"
                onChange={handleFileChange}
                className="upload-modal__file-input"
                id="geojson-upload"
              />
              <label className="upload-modal__file-input__cta" htmlFor="geojson-upload" onClick={() => setErrorMessage(null)}>Browse</label>
            </div>
            <div className="upload-modal__upload-btn__wrapper">
              <span className="upload-modal__info">
                Your file must be a geojson with polygons using the WGS 84 (EPSG 4326)
                coordinate system and can have a maximum size of 5MB
              </span>
              <Button
                css={{
                  borderRadius: '4px',
                  padding: '6px',
                  height: '40px',
                }}
                disabled={!fileName}
                onClick={mutate}
                isLoading={isPending}
              >
                Upload
              </Button>
            </div>
          </div>
          
        </div>
      </div>
    </Portal>
  )
}

export default UploadModal